import React from 'react';
import { Box, Modal, Typography, Tabs, Tab } from '@mui/material';
import ProgramListImg from '@client/new/pages/LandingPagesBuilder/assets/ProgramsList.png';
import PaidProgramListImg from '@client/new/pages/LandingPagesBuilder/assets/PaidProgramsList.png';
import TextSectionImg from '@client/new/pages/LandingPagesBuilder/assets/TextSection.png';
import TextSection1Img from '@client/new/pages/LandingPagesBuilder/assets/TextSection1.png';
import TextSection2Img from '@client/new/pages/LandingPagesBuilder/assets/TextSection2.png';
import BioImg from '@client/new/pages/LandingPagesBuilder/assets/Bio.png';
import TestimonialsImg from '@client/new/pages/LandingPagesBuilder/assets/Testimonials.png';
import ReviewsImg from '@client/new/pages/LandingPagesBuilder/assets/Reviews.png';
import LogosImg from '@client/new/pages/LandingPagesBuilder/assets/Logos.png';
import HighlightedProgramImg from '@client/new/pages/LandingPagesBuilder/assets/HighlightedProgram.png';
import FeaturedBenefitImg from '@client/new/pages/LandingPagesBuilder/assets/FeaturedBenefit.png';
import FeaturesImg from '@client/new/pages/LandingPagesBuilder/assets/Features.png';
import EmailCaptureImg from '@client/new/pages/LandingPagesBuilder/assets/EmailCapture.png';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { ClickableIcon } from '@client/new/components/basic';
import { PrimaryButton } from '@client/new/components/button';
import classnames from 'classnames';
import { ISection } from '@playbook-technologies/common-schemas';
import { useCreator } from '../LandingPageBuilder';
import { compact, isEmpty } from 'lodash';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '688px',
  bgcolor: 'background.paper',
  border: 'none',
  borderRadius: '10px',
  p: 4,
  outline: 'none',
};

type Option = {
  value: ISection['type'];
  label: string;
  image?: string;
  video?: string;
};

type SelectModalProps = {
  onSelect: (o: ISection['type']) => void;
  onClose: () => void;
};

export const BlockSelectModal = ({ onSelect, onClose }: SelectModalProps) => {
  const [selectedOption, onSelectOption] = React.useState<null | Option>(null);

  const handleSelect = (o: Option | null) => {
    onSelectOption(o);

    if (!o) return;

    onSelect(o.value);
    onClose();
  };

  // https://mui.com/material-ui/react-modal/
  return (
    <Modal
      open={true}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="relative">
          <ClickableIcon
            warn
            icon={faClose}
            className="text-2xl absolute right-0 top-[-10px]"
            onClick={onClose}
            title="Close"
          />
          <Typography
            id="modal-modal-title"
            variant="h4"
            component="h4"
            textAlign="center"
          >
            Add new section
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <section className="flex flex-wrap gap-6">
              <SelectBlock value={selectedOption} onSelect={onSelectOption} />
              <PrimaryButton
                disabled={selectedOption === null}
                className="w-full mt-5 h-10 text-lg"
                onClick={() => handleSelect(selectedOption)}
              >
                Insert
              </PrimaryButton>
            </section>
          </Typography>
        </div>
      </Box>
    </Modal>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div>
          <Typography>{children}</Typography>
        </div>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function SelectBlock({
  value,
  onSelect,
}: {
  value: Option | null;
  onSelect: (o: Option | null) => void;
}) {
  const [tabValue, setValue] = React.useState(0);
  const creator = useCreator();
  const isOTPOnly =
    !isEmpty(creator.publishedPrograms) &&
    creator.publishedPrograms.every((p) => p.accessType === 'exclusive');

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const contentOptions: Option[] = compact([
    isOTPOnly
      ? null
      : {
          value: 'ProgramsList',
          label: 'Subscription Programs Carousel',
          image: ProgramListImg,
        },
    {
      value: 'PaidProgramsList',
      label: 'One-time Purchases Carousel',
      image: PaidProgramListImg,
    },
    {
      value: 'TextSection',
      label: 'Headline + text',
      image: TextSectionImg,
    },
    {
      value: 'TextSection1',
      label: 'Headline + text + media',
      image: TextSection1Img,
    },
    {
      value: 'TextSection2',
      label: 'Headline + text + two images',
      image: TextSection2Img,
    },
  ]);

  const promotionOptions: Option[] = [
    {
      value: 'EmailCapture',
      label: 'Email Pop-Up',
      image: EmailCaptureImg,
    },
    { value: 'Bio', label: 'Bio', image: BioImg },
    {
      value: 'Testimonials',
      label: 'Testimonials',
      image: TestimonialsImg,
    },
    {
      value: 'Reviews',
      label: 'Reviews',
      image: ReviewsImg,
    },
    {
      value: 'Logos',
      label: 'Publishers',
      image: LogosImg,
    },
    {
      value: 'ProgramsHighlight',
      label: 'Featured program',
      image: HighlightedProgramImg,
    },
    {
      value: 'FeaturedBenefit',
      label: 'Featured benefit',
      image: FeaturedBenefitImg,
    },
    {
      value: 'FeaturesNew',
      label: 'App features',
      image: FeaturesImg,
    },
  ];

  return (
    <div className="w-full">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Promotion" {...a11yProps(0)} />
          <Tab label="Content" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        <section className="flex flex-wrap gap-6 overflow-y-scroll max-h-[400px]">
          {promotionOptions.map((o) => (
            <BlockOption
              o={o}
              value={value}
              onSelect={onSelect}
              key={o.label}
            />
          ))}
        </section>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <section className="flex flex-wrap gap-6 overflow-y-scroll max-h-[400px]">
          {contentOptions.map((o) => (
            <BlockOption
              o={o}
              value={value}
              onSelect={onSelect}
              key={o.label}
            />
          ))}
        </section>
      </TabPanel>
    </div>
  );
}

function BlockOption({
  o,
  onSelect,
  value,
}: {
  o: Option;
  onSelect: (o: Option) => void;
  value: Option | null;
}) {
  return (
    <div key={o.value} onClick={() => onSelect(o)} className="cursor-pointer">
      <div
        className={classnames(
          'h-[200px] w-[260px] flex items-center justify-center border rounded-lg',
          o.label === value?.label ? ' border-primary' : ' border-gray-200',
        )}
      >
        <img
          className={classnames('cursor-pointer max-h-[160px] w-auto')}
          src={o.image}
          alt={o.label}
        />
      </div>

      <Typography
        variant="h5"
        component="div"
        className={classnames(
          'mt-2',
          o.label === value?.label ? 'text-primary' : '',
        )}
      >
        {o.label}
      </Typography>
    </div>
  );
}
